import { Link } from "react-router-dom";

const VALUES = [
    {
        n: "01",
        t: "Trust",
        d: "We treat every property like it's our own, and keep owners in the loop on what's happening — bookings, cleans, the lot.",
    },
    {
        n: "02",
        t: "Care",
        d: "From the way the linen is folded to how we reply to guests, we sweat the small stuff. That's what gets five-star reviews.",
    },
    {
        n: "03",
        t: "Hospitality",
        d: "Every guest is treated well — quick replies, helpful tips, a clean and ready home. Happy guests come back and recommend us.",
    },
    {
        n: "04",
        t: "Partnership",
        d: "Our fee is performance-based, so we only do well when your property does. That keeps us focused on what actually matters.",
    },
];

export default function About() {
    return (
        <div className="bg-night">
            {/* Hero */}
            <section
                className="pt-40 pb-20 md:pt-48 md:pb-28"
                data-testid="about-hero"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <div className="eyebrow mb-6">About Leo&rsquo;s Luxury</div>
                        <h1 className="headline text-5xl md:text-7xl lg:text-[88px] text-cream leading-[1.02]">
                            A boutique<br />
                            short-stay agency<br />
                            <span className="italic text-gold">built in Melbourne.</span>
                        </h1>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                        <p className="text-cream/55 font-light text-lg leading-relaxed">
                            We started Leo&rsquo;s Luxury because we believed Melbourne
                            property owners deserved a more personal, more hands-on
                            kind of short-stay management — built on trust, care
                            and genuine hospitality. We&rsquo;re a young, owner-run
                            business based in Melbourne&rsquo;s west.
                        </p>
                    </div>
                </div>
            </section>

            {/* Founder section */}
            <section
                className="py-20 md:py-28 bg-surface"
                data-testid="about-founder"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <div className="editorial-img aspect-[4/5] overflow-hidden border border-line-dark">
                            <img
                                src="/brand/founder-leonce.jpg"
                                alt="Léonce — Founder, Leo's Luxury"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <div className="eyebrow mb-4">Founder &amp; Host</div>
                        <h2 className="headline text-4xl md:text-6xl text-cream mb-6">
                            Léonce
                        </h2>
                        <p className="text-cream/55 font-light text-lg leading-relaxed mb-6">
                            Léonce founded Leo&rsquo;s Luxury to bring genuine,
                            hotel-grade hospitality to short-stay hosting across
                            Melbourne&rsquo;s western suburbs. Every property is
                            prepared with fresh linen, professional cleaning, and
                            the kind of personal attention that turns guests into
                            repeat visitors.
                        </p>
                        <p className="text-cream/55 font-light text-lg leading-relaxed">
                            Speaking English and Swahili, with a 100% host response
                            rate and replies typically within an hour, Léonce takes
                            care of both owners and guests the same way — direct
                            communication, clear reporting, and respect for every
                            property he hosts.
                        </p>
                        <blockquote className="font-serif italic text-2xl text-cream mt-10 pl-6 border-l border-gold">
                            &ldquo;We manage boutique-style homes across Melbourne&rsquo;s
                            western suburbs — every stay prepared to hotel standards,
                            every owner treated like a partner.&rdquo;
                        </blockquote>
                    </div>
                </div>
            </section>

            {/* Mission */}
            <section className="py-24 md:py-32" data-testid="about-mission">
                <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
                    <div className="eyebrow mb-6">Our Mission</div>
                    <h2 className="headline text-4xl md:text-6xl text-cream leading-[1.05]">
                        To make short-stay hosting{" "}
                        <span className="italic text-gold">
                            easier and more rewarding
                        </span>{" "}
                        for every owner we work with.
                    </h2>
                </div>
            </section>

            {/* Values */}
            <section
                className="py-24 md:py-32 bg-[#0A0A0A] text-cream relative overflow-hidden"
                data-testid="about-values"
            >
                <div className="grain absolute inset-0" />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-5">
                            <div className="eyebrow text-gold mb-4">How we work</div>
                            <h2 className="headline text-4xl md:text-6xl">
                                What we<br />care about.
                            </h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14">
                        {VALUES.map((v, i) => (
                            <div
                                key={v.n}
                                className="reveal"
                                style={{ transitionDelay: `${i * 100}ms` }}
                                data-testid={`value-${v.n}`}
                            >
                                <div className="font-serif text-6xl md:text-7xl text-gold/40 mb-2">
                                    {v.n}
                                </div>
                                <div className="hairline mb-5 opacity-40" />
                                <h3 className="font-serif text-3xl mb-3 text-cream">{v.t}</h3>
                                <p className="text-cream/70 font-light leading-relaxed max-w-md">
                                    {v.d}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-24 md:py-32 bg-surface" data-testid="about-why-us">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-5">
                            <div className="eyebrow mb-4">Why Choose Leo&rsquo;s Luxury</div>
                            <h2 className="headline text-4xl md:text-6xl text-cream">
                                Small, local,<br />and hands-on.
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 text-cream/55 font-light text-lg leading-relaxed">
                            <p>
                                We&rsquo;re an owner-run business based in
                                Melbourne&rsquo;s west. That means you talk to
                                the person actually managing your property — not
                                a call centre — and every decision is made with
                                your home in mind.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line-dark border border-line-dark">
                        {[
                            ["Local Knowledge", "Based in Melbourne's west — we know the area, the suburbs, the kind of guest each home attracts."],
                            ["Hands-On Service", "Small enough that every property gets real attention. You'll always know who's looking after your home."],
                            ["Owner-First Fees", "Performance-based pricing, transparent reporting, no hidden costs."],
                        ].map(([t, d], i) => (
                            <div
                                key={t}
                                className="bg-night p-10 md:p-12 reveal"
                                style={{ transitionDelay: `${i * 80}ms` }}
                            >
                                <h3 className="font-serif text-3xl text-cream mb-4">{t}</h3>
                                <p className="text-cream/55 font-light leading-relaxed">
                                    {d}
                                </p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-16 text-center">
                        <Link to="/contact" className="btn-gold" data-testid="about-cta">
                            Start the Conversation
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
